import { useCallback } from 'react';
import type { JSX } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import { PETITION_COUNT_PER_ROW } from 'src/app/pages/movement/shared/constants';
import type { Petition } from 'src/app/pages/movement/shared/types';

import { MovementPetitionCard } from '../components/petition/MovementPetitionCard';
import { useGetPetitionsInMovement } from '../hooks/useGetPetitionsInMovement';

type MovementPetitionsContainerProps = {
	movementId: string;
	onPetitionRemoved: () => void;
};

export function MovementPetitionsContainer({
	movementId,
	onPetitionRemoved,
}: MovementPetitionsContainerProps): JSX.Element | null {
	const { petitionData, fetchMore, removePetition } = useGetPetitionsInMovement(movementId);

	const onRemove = useCallback(
		async (id: string) => {
			if (await removePetition(id)) {
				onPetitionRemoved();
			}
		},
		[removePetition, onPetitionRemoved],
	);

	if (!petitionData?.items.length) return null;

	return (
		<>
			<Box my={16}>
				<Heading size="h3" as="h3">
					<Translate value="corgi.movement.show.petition_list_heading" />
				</Heading>
			</Box>
			<Box mx={-8} data-qa="suggested-petitions">
				{petitionData.items.map((petition: Petition) => (
					<Box
						key={`petition-${petition.id}`}
						sx={{
							display: 'inline-block',
							width: ['100%', `${100 / (PETITION_COUNT_PER_ROW - 1)}%`, `${100 / PETITION_COUNT_PER_ROW}%`],
							verticalAlign: 'top',
						}}
						px={8}
						mb={16}
					>
						<MovementPetitionCard petition={petition} onRemove={onRemove} />
					</Box>
				))}
			</Box>
			<Box sx={{ textAlign: 'center' }}>
				{petitionData.lastPage || (
					<Button
						type="button"
						variant="secondary"
						data-testid="load-more-petitions"
						data-qa="load-more-petitions"
						mr={[0, 16]}
						onClick={fetchMore}
					>
						<Translate value="corgi.movement.show.load_more_button" />
					</Button>
				)}
			</Box>
		</>
	);
}
